import React, { useRef } from 'react';
import { RadioButton, Select } from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { Box, Button, styled, TextField, useTheme } from '@mui/material';
import { differenceInCalendarDays } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { RelativeDatePeriod, RelativeDateType } from './AvDateRangePicker.constants';
import { DateRangeValueType, PredefinedPreset, Preset } from './types';
import { captionComponent, customPreset, getPresets, isDateRangeValue, stringDefinition } from './utils';

export const ArrowLeft: any = styled(ArrowDown)`
  transform: rotate(90deg);
  width: 14px;
`;
export const ArrowRight: any = styled(ArrowDown)`
  transform: rotate(-90deg);
  width: 14px;
`;

export const NotSetOption = ({ value, onChange }: { value: string; onChange: (v) => void }) => (
  <Box sx={{ ...flex.col, py: 1, px: 2 }}>
    <RadioButton
      options={[
        { title: 'Date Range', value: 'relative' },
        { title: 'Is Empty', value: 'empty' },
        { title: 'Is Not Empty', value: 'notEmpty' },
      ]}
      value={value}
      onChange={onChange}
    />
  </Box>
);

export const RelativeDateOptions = ({
  allowFutureDates,
  isNull,
  isPreset,
  onPresetClick,
  preset,
  predefinedPresets,
}: {
  allowFutureDates: boolean;
  isNull: boolean;
  isPreset: boolean;
  onPresetClick: (preset) => void;
  preset?: Preset;
  predefinedPresets?: PredefinedPreset[];
}) => {
  const isCustomPreset =
    !isPreset ||
    (isPreset &&
      (predefinedPresets?.length ? predefinedPresets : getPresets({ allowFutureDates })).every(
        p => stringDefinition(p.definition) !== stringDefinition(preset)
      ));
  return (
    <Box sx={{ ...flex.colJustifyCenter, gap: 1, py: 2, pl: 2, pr: 2, ...(!allowFutureDates && flex.justifyStart) }}>
      {(predefinedPresets || [...getPresets({ allowFutureDates }), { title: customPreset }]).map(p => (
        <Button
          key={p.title}
          disabled={isNull}
          size="small"
          sx={
            (isCustomPreset && p.title === customPreset) || (isPreset && stringDefinition(preset) === stringDefinition(p.definition))
              ? {
                  justifyContent: 'flex-start',
                  fontWeight: 400,
                  pointerEvents: 'none',
                  color: 'var(--color-content-interactive-primary-default)',
                  background: 'var(--color-surface-nav-sideBar-active)',
                }
              : { justifyContent: 'flex-start', fontWeight: 400 }
          }
          onClick={() => onPresetClick(p)}>
          {p.title}
        </Button>
      ))}
    </Box>
  );
};

export const DynamicRangeSelect = ({
  inputFocus,
  isNull,
  disablePortal,
  onBlur,
  updatePreset,
  preset,
  predicate,
  showMinutesAndHours = false,
}: {
  isNull: boolean;
  disablePortal: boolean;
  predicate: ({ value }: { value: any }) => boolean;
  preset?: Preset;
  inputFocus: boolean;
  onBlur: () => void;
  updatePreset: (key: string, value: any) => void;
  showMinutesAndHours?: boolean;
}) => {
  const theme = useTheme();
  const countInputRef = useRef<any>(undefined);
  const periodOptions = [
    ...(showMinutesAndHours
      ? [
          { title: 'Minutes', value: RelativeDatePeriod.minutes },
          { title: 'Hours', value: RelativeDatePeriod.hours },
        ]
      : []),
    { title: 'Days', value: RelativeDatePeriod.days },
    { title: 'Weeks', value: RelativeDatePeriod.weeks },
    { title: 'Months', value: RelativeDatePeriod.months },
  ];

  const dateTypeOptions = [
    { title: 'Next', value: RelativeDateType.next },
    { title: 'Last', value: RelativeDateType.last },
    { title: 'Previous', value: RelativeDateType.previous },
    { title: 'Before', value: RelativeDateType.before },
  ];

  return (
    <Box
      // @ts-ignore
      sx={{
        ...flex.itemsCenter,
        gap: 1,
        pl: '22px',
        ...(isNull ? { display: 'none' } : {}),
        '.MuiPaper-root': theme?.components?.MuiAutocomplete?.styleOverrides?.paper,
      }}>
      <Box sx={{ pr: '2px' }}>Dynamic Range:</Box>
      <Select
        muiProps={{ disablePortal }}
        style={{ width: 124 }}
        isRequired
        size="small"
        options={dateTypeOptions.filter(predicate)}
        value={preset?.type}
        onChange={v => updatePreset('type', v)}
      />
      <TextField
        sx={{ width: 72 }}
        autoComplete="off"
        type="number"
        focused={inputFocus}
        onBlur={onBlur}
        inputProps={{ min: 1 }}
        required
        ref={countInputRef}
        size="small"
        value={preset?.count || ''}
        onChange={e => updatePreset('count', +e.target.value)}
      />
      <Select
        muiProps={{ disablePortal }}
        style={{ width: 124 }}
        isRequired
        size="small"
        options={periodOptions}
        value={preset?.period}
        onChange={v => updatePreset('period', v)}
      />
    </Box>
  );
};

export const CalenderPicker = ({
  date,
  isRange,
  onSelect,
  month,
  onMonthChange,
  allowFutureDates,
  allowPastDatesOnDayPicker,
  timeOptions,
  onChangeTimeOptions,
}: {
  onSelect: (v) => void;
  date: DateRangeValueType;
  isRange: boolean;
  month: Date | null;
  onMonthChange: (value: Date) => void;
  allowFutureDates: boolean;
  allowPastDatesOnDayPicker: boolean;
  timeOptions: any[] | undefined;
  onChangeTimeOptions: (v) => void;
}) => (
  <>
    <DayPicker
      components={{
        CaptionLabel: captionComponent,
        IconLeft: ArrowLeft,
        IconRight: ArrowRight,
      }}
      onSelect={onSelect}
      selected={!isDateRangeValue(date) ? undefined : isRange ? date : date?.from}
      month={month === null ? undefined : month}
      onMonthChange={onMonthChange}
      disabled={
        allowPastDatesOnDayPicker
          ? allowFutureDates
            ? undefined // All dates are allowed
            : date => differenceInCalendarDays(date, new Date()) > 0 // Allow past dates, disable future dates
          : allowFutureDates
            ? date => differenceInCalendarDays(date, new Date()) < 0 // Allow future dates, disable past dates
            : date => differenceInCalendarDays(date, new Date()) !== 0 // Enable only today
      }
      mode={(isRange ? 'range' : 'single') as any}
      numberOfMonths={isRange ? 2 : 1}
    />
    {timeOptions && (
      <Box sx={{ ...flex.center, gap: 1, pt: 1 }}>
        <div>Time:</div>
        <Select
          style={{ width: 150 }}
          onChange={onChangeTimeOptions}
          isRequired
          options={timeOptions}
          size="small"
          value={isDateRangeValue(date) ? `${date.from.getHours()}:${date.from.getMinutes()}` : ':'}
        />
      </Box>
    )}
  </>
);
